import { ComponentType, ReactElement, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Redirect, useRouteMatch } from 'react-router';
import { Stack } from '@rossum/ui/material';
import { UnpaidFeatureOverlay } from '../../features/pricing/components/UnpaidFeatureOverlay';
import {
  useExtensionLogsFeatureSubscription,
  useExtensionsFeatureSubscription,
} from '../../features/pricing/hooks/useExtensionsFeatureSubscription';
import {
  approvalWorkflowsFeatureSelector,
  embeddedFeatureSelector,
  labelsFeatureSelector,
  statisticsFeatureSelector,
} from '../../features/pricing/selectors';
import { safeOrganizationSelector } from '../../redux/modules/organization/selectors';
import { datasetsFeatureSelector } from '../../redux/modules/organizationGroup/selectors';
import {
  isUserOfRoleSelector,
  userRoleNameSelector,
} from '../../redux/modules/user/selectors';
import { GroupRole } from '../../types/group';
import { State } from '../../types/state';

type OwnProps = {
  children: ReactNode;
  restrictComponent?: ReactElement | null;
};
type StateProps = { enabled?: boolean; inconclusive?: boolean };
type Props = OwnProps & StateProps;

export type RoleProps = { role: GroupRole };

type RoleRestrictorProps = {
  requiredRoles: Array<GroupRole>;
  isFeatureEnabled?: (_state: State) => boolean;
  resolveConclusiveness?: (_state: State) => boolean;
};

const Restrictor = ({
  children,
  enabled,
  restrictComponent = null,
  inconclusive = true,
}: Props) =>
  inconclusive ? null : enabled ? restrictComponent : <>{children}</>;

export const RoleRestrictor = connect<
  StateProps,
  null,
  OwnProps & RoleRestrictorProps,
  State
>(
  (
    state,
    {
      requiredRoles,
      resolveConclusiveness = () => true,
      isFeatureEnabled = () => true,
    }
  ) => {
    const enabled = !(
      requiredRoles.some(role => isUserOfRoleSelector(state)(role)) &&
      isFeatureEnabled(state)
    );
    const inconclusive = !(state.groups.length && resolveConclusiveness(state));

    return {
      enabled,
      inconclusive,
    };
  }
)(Restrictor);

export const ViewerRestrictor = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={[
      'admin',
      'organization_group_admin',
      'manager',
      'empowered_manager',
      'annotator',
      'annotator_limited',
    ]}
    {...props}
  />
);

export const AnnotatorLimitedRestrictor = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={[
      'admin',
      'organization_group_admin',
      'manager',
      'empowered_manager',
      'annotator',
      'viewer',
      'approver',
    ]}
    {...props}
  />
);

export const canUploadRoles: GroupRole[] = [
  'admin',
  'organization_group_admin',
  'manager',
  'empowered_manager',
  'annotator',
];

export const UploadRestrictor = (props: OwnProps) => (
  <RoleRestrictor requiredRoles={canUploadRoles} {...props} />
);

export const EditDocumentsRestrictor = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={[
      'admin',
      'organization_group_admin',
      'manager',
      'empowered_manager',
      'annotator',
      'annotator_embedded',
    ]}
    {...props}
  />
);

// TODO: Rename to AdminRestrictor - name is misleading. Restricts TO admins, not FROM non-admins.
export const NonAdminRestrictor = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={['admin', 'organization_group_admin']}
    {...props}
  />
);

export const SettingsRestrictor = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={['admin', 'organization_group_admin', 'empowered_manager']}
    {...props}
  />
);

export const AdminAndManagerRestrictor = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={[
      'admin',
      'organization_group_admin',
      'manager',
      'empowered_manager',
    ]}
    {...props}
  />
);

export const RoleProvider = connect((state: State) => ({
  role: userRoleNameSelector(state),
}))(
  ({
    children,
    role,
  }: {
    children: ({ role }: RoleProps) => ReactElement | null;
    role: GroupRole;
  }) => children({ role })
);

export const queueSettingsRestrictorRoute =
  <T,>(Route: ComponentType<T>) =>
  (routeProps: T & JSX.IntrinsicAttributes) => {
    return (
      <RoleRestrictor
        requiredRoles={[
          'admin',
          'organization_group_admin',
          'empowered_manager',
        ]}
        restrictComponent={<Redirect to="/error?reason=no-permission" />}
      >
        <Route {...routeProps} />
      </RoleRestrictor>
    );
  };

const StatisticsGuard = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={[
      'admin',
      'organization_group_admin',
      'manager',
      'empowered_manager',
    ]}
    resolveConclusiveness={state =>
      !!safeOrganizationSelector(state)?.id &&
      !!state.organizationGroup.current?.id
    }
    isFeatureEnabled={statisticsFeatureSelector}
    {...props}
  />
);

export const StatisticsRouteGuard = ({ children }: OwnProps) => {
  const intl = useIntl();
  return (
    <StatisticsGuard
      restrictComponent={
        <Stack height={1}>
          <UnpaidFeatureOverlay
            title={intl.formatMessage({
              id: 'features.pricing.unpaidFeatureOverlay.title.statistics',
            })}
            dataCy="unpaid-overlay-statistics"
          />
        </Stack>
      }
    >
      {children}
    </StatisticsGuard>
  );
};

export const EmbeddedModeGuard = (props: OwnProps) => {
  const intl = useIntl();

  return (
    <RoleRestrictor
      requiredRoles={[
        'annotator',
        'annotator_limited',
        'annotator_embedded',
        'manager',
        'empowered_manager',
        'admin',
        'viewer',
        'organization_group_admin',
        'approver',
      ]}
      resolveConclusiveness={state =>
        !!safeOrganizationSelector(state)?.id &&
        !!state.organizationGroup.current?.id
      }
      isFeatureEnabled={embeddedFeatureSelector}
      restrictComponent={
        <Stack height={1}>
          <UnpaidFeatureOverlay
            title={intl.formatMessage({
              id: 'features.pricing.unpaidFeatureOverlay.title.embeddedMode',
            })}
            dataCy="unpaid-overlay-embedded-mode"
          />
        </Stack>
      }
      {...props}
    />
  );
};

export const LabelsGuard = (props: OwnProps) => (
  <RoleRestrictor
    requiredRoles={['admin', 'organization_group_admin', 'empowered_manager']}
    resolveConclusiveness={state =>
      !!safeOrganizationSelector(state)?.id &&
      !!state.organizationGroup.current?.id
    }
    isFeatureEnabled={labelsFeatureSelector}
    restrictComponent={<Redirect to="/documents" />}
    {...props}
  />
);

export const ExtensionLogsGuard = ({ children }: OwnProps) => {
  const intl = useIntl();
  const logsSubscription = useExtensionLogsFeatureSubscription();

  return (
    <RoleRestrictor
      requiredRoles={['admin', 'organization_group_admin']}
      resolveConclusiveness={state =>
        !!safeOrganizationSelector(state)?.id &&
        !!state.organizationGroup.current?.id &&
        logsSubscription.status === 'success'
      }
      isFeatureEnabled={() => logsSubscription.canAccess}
      restrictComponent={
        <Stack
          height={1}
          sx={{ pt: 10, '> div': { backgroundColor: 'transparent' } }}
        >
          <UnpaidFeatureOverlay
            title={intl.formatMessage({
              id: 'features.pricing.unpaidFeatureOverlay.title.extensionLogs',
            })}
            dataCy="unpaid-overlay-extension-logs"
          />
        </Stack>
      }
    >
      {children}
    </RoleRestrictor>
  );
};

export const RulesExecutionLogsGuard = ({ children }: OwnProps) => (
  <RoleRestrictor requiredRoles={['admin', 'organization_group_admin']}>
    {children}
  </RoleRestrictor>
);

export const ExtensionsCreateGuard = ({ children }: OwnProps) => (
  <RoleRestrictor
    requiredRoles={['admin', 'organization_group_admin']}
    resolveConclusiveness={state =>
      !!safeOrganizationSelector(state)?.id &&
      !!state.organizationGroup.current?.id
    }
  >
    {children}
  </RoleRestrictor>
);

export const ExtensionsGuard = ({ children }: OwnProps) => {
  const extensionsSubscription = useExtensionsFeatureSubscription();

  return (
    <RoleRestrictor
      requiredRoles={['admin', 'organization_group_admin']}
      resolveConclusiveness={state =>
        !!safeOrganizationSelector(state)?.id &&
        !!state.organizationGroup.current?.id &&
        extensionsSubscription.status === 'success'
      }
    >
      {children}
    </RoleRestrictor>
  );
};

export const ApprovalWorkflowsGuard = (props: OwnProps) => {
  const intl = useIntl();
  const match = useRouteMatch('/requests/:id');

  return (
    <RoleRestrictor
      requiredRoles={['admin', 'organization_group_admin', 'approver']}
      resolveConclusiveness={state =>
        !!safeOrganizationSelector(state)?.id &&
        !!state.organizationGroup.current?.id
      }
      isFeatureEnabled={approvalWorkflowsFeatureSelector}
      restrictComponent={
        <Stack height={1}>
          <UnpaidFeatureOverlay
            title={intl.formatMessage({
              id: 'features.pricing.unpaidFeatureOverlay.title.approvalWorkflows',
            })}
            dataCy="unpaid-overlay-approval-workflows"
            showReturnButton={match?.isExact}
          />
        </Stack>
      }
      {...props}
    />
  );
};

export const ConfigurationChangelogGuard = (props: OwnProps) => {
  return (
    <RoleRestrictor
      requiredRoles={['admin', 'organization_group_admin']}
      resolveConclusiveness={state =>
        !!safeOrganizationSelector(state)?.id &&
        !!state.organizationGroup.current?.id
      }
      restrictComponent={<Redirect to="/settings" />}
      {...props}
    />
  );
};

export const DatasetsGuard = (props: OwnProps) => {
  const intl = useIntl();
  return (
    <RoleRestrictor
      requiredRoles={['admin', 'organization_group_admin']}
      resolveConclusiveness={state =>
        !!safeOrganizationSelector(state)?.id &&
        !!state.organizationGroup.current?.id
      }
      isFeatureEnabled={datasetsFeatureSelector}
      restrictComponent={
        <Stack height={1}>
          <UnpaidFeatureOverlay
            title={intl.formatMessage({
              id: 'features.pricing.unpaidFeatureOverlay.title.datasets',
            })}
            dataCy="unpaid-overlay-dataset-management"
          />
        </Stack>
      }
      {...props}
    />
  );
};
